import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { ButtonIconTW } from "../../../components/Button/Button"
import {
  faBoxOpen,
  faClock,
  faHouse,
  faLocationDot,
  faSquareArrowUpRight,
  faMoneyBillWave,
  faTruckFast,
  faShareNodes,
  faUmbrellaBeach,
  faBullhorn,
  faStickyNote,
  faQrcode,
  faSliders,
  faGears,
} from "@fortawesome/free-solid-svg-icons"
import Animate from "../../../components/Animate/Animate"
import GetSpace from "../../../utils/space"
import { CompanyDataPupop } from "./CompanyDataPupop"
import { TitleCardTW } from "../../../components/TitleCardTW/TitleCardTW"
import { CompanyAddressPupop } from "./CompanyAddressPupop"
import { OperatingSchedulePopup } from "./OperatingSchedulePopup"
import { LinkPublicMenuPupop } from "./LinkPublicMenuPupop"
import { BillingPopup } from "./BillingPopup"
import { FreeShippingSettingsPupop } from "./FreeShippingSettingsPupop"
import { useUpgradePlan } from "../../../context/useUpgradePlanContext"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { PublicMenuQrCodePupop } from "./PublicMenuQrCodePupop"
import { SocialMediaPupop } from "./SocialMediaPupop"
import { WhatsappOrderSupportPopup } from "./WhatsappOrderSupportPopup"
import { PublicMenuCustomizationPopup } from "./PublicMenuCustomizationPopup"
import { Building2, LayoutTemplate, Link2, Store } from "lucide-react"
import { useMessage } from "../../../context/useMessageContext"
import { useError } from "../../../context/useErrorContext"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { PATH_MAP } from "../../../configuration/PathMap"
import { VacationSettingsPopup } from "./VacationSettingsPopup"
import { MarketingTrackingPopup } from "./MarketingTrackingPopup"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { CompanyModulesPopup } from "../../../components/Popups/CompanyModulesPopup/CompanyModulesPopup"
import { OperationalSettingsPopup } from "./OperationalSettingsPopup"

type CategoryHeaderProps = {
  icon: React.ReactNode
  title: string
  description: string
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({ icon, title, description }) => (
  <div className="mb-2 mt-4 px-1">
    <div className="flex items-center gap-3">
      <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 via-orange-100 to-red-100 text-orange-500">
        {icon}
      </span>

      <p className="text-md font-bold tracking-wide text-gray-900">{title}</p>
    </div>
    <p className="mt-1 text-sm text-slate-500">{description}</p>
    <div className="mt-3 h-px w-full bg-slate-200" />
  </div>
)

export const MyCompanyPage: React.FC = () => {
  const { t } = useTranslation("company")

  const { setMessage } = useMessage()

  const { setError } = useError()

  const { handleGetCompany } = useGetCompany()

  const navigate = useNavigate()

  const location = useLocation()

  const [searchParams] = useSearchParams()

  const onboardingStep = searchParams.get("onboardingStep")

  const lastOnboardingOpenKeyRef = useRef<string | null>(null)

  const [isPublicMenuEnable, setPublicMenuEnable] = useState(false)

  const { userEmployee } = useEmployerLogged()

  const [showSchedule, setShowSchedule] = useState(false)

  const [isCompanyEnable, setEnableCompany] = useState(false)

  const [isCompanyAddressEnable, setCompanyAddressEnable] = useState(false)

  const [isShippingSettingsOpen, setShippingSettingsOpen] = useState(false)

  const [isOperationalSettingsOpen, setOperationalSettingsOpen] = useState(false)

  const [isPlanAndBillingOpen, setPlanAndBillingOpen] = useState(false)

  const [isPublicMenuQrCodeOpen, setPublicMenuQrCodeOpen] = useState(false)

  const [isPublicMenuCustomizationOpen, setPublicMenuCustomizationOpen] = useState(false)

  const [isMarketingTrackingOpen, setMarketingTrackingOpen] = useState(false)

  const [isSocialMediaOpen, setSocialMediaOpen] = useState(false)

  const [isWhatsappOrderSupportOpen, setWhatsappOrderSupportOpen] = useState(false)

  const [isVacationSettingsOpen, setVacationSettingsOpen] = useState(false)

  const [isCompanyModulesOpen, setCompanyModulesOpen] = useState(false)

  const { openPlan, closePlan } = useUpgradePlan()

  const closeCompanyPopups = useCallback(() => {
    setEnableCompany(false)
    setSocialMediaOpen(false)
    setWhatsappOrderSupportOpen(false)
    setPublicMenuEnable(false)
    setPublicMenuCustomizationOpen(false)
    setMarketingTrackingOpen(false)
    setPublicMenuQrCodeOpen(false)
    setCompanyAddressEnable(false)
    setShippingSettingsOpen(false)
    setOperationalSettingsOpen(false)
    setShowSchedule(false)
    setVacationSettingsOpen(false)
    setCompanyModulesOpen(false)
    setPlanAndBillingOpen(false)
    closePlan()
  }, [closePlan])

  useEffect(() => {
    const oauthStatus = searchParams.get("mp-oauth")

    const oauthMessage = searchParams.get("message")

    if (!oauthStatus) {
      return
    }

    const normalizedMessage = oauthMessage?.trim()

    const clearOAuthParams = () => navigate(PATH_MAP.COMPANY_PAGE, { replace: true })

    if (oauthStatus === "success") {
      void handleGetCompany(
        () => {
          setPlanAndBillingOpen(true)
          setMessage(t("label_mp_oauth_success_title"), normalizedMessage || t("label_mp_oauth_success_description"))
        },
        undefined,
        false,
      ).finally(() => {
        clearOAuthParams()
      })

      return
    }

    setPlanAndBillingOpen(true)

    setError(t("label_mp_oauth_error_title"), normalizedMessage || t("label_mp_oauth_error_description"))

    clearOAuthParams()
  }, [handleGetCompany, navigate, searchParams, setError, setMessage, t])

  useEffect(() => {
    window.addEventListener("close-owner-onboarding-popups", closeCompanyPopups)

    return () => window.removeEventListener("close-owner-onboarding-popups", closeCompanyPopups)
  }, [closeCompanyPopups])

  useEffect(() => {
    if (!onboardingStep) {
      lastOnboardingOpenKeyRef.current = null

      return
    }

    const openKey = `${location.key}:${location.pathname}:${location.search}:${onboardingStep}`

    if (lastOnboardingOpenKeyRef.current === openKey) {
      return
    }

    lastOnboardingOpenKeyRef.current = openKey

    setEnableCompany(onboardingStep === "company-data")

    setSocialMediaOpen(onboardingStep === "social-media")

    setWhatsappOrderSupportOpen(onboardingStep === "whatsapp-support")

    setPublicMenuEnable(onboardingStep === "public-menu")

    setPublicMenuCustomizationOpen(onboardingStep === "public-menu-customization")

    setMarketingTrackingOpen(onboardingStep === "marketing-tracking")

    setPublicMenuQrCodeOpen(onboardingStep === "public-menu-qr")

    setCompanyAddressEnable(onboardingStep === "address")

    setShippingSettingsOpen(onboardingStep === "shipping")

    setOperationalSettingsOpen(onboardingStep === "operational")

    setShowSchedule(onboardingStep === "schedule")

    setVacationSettingsOpen(onboardingStep === "vacation")

    setCompanyModulesOpen(onboardingStep === "modules")

    setPlanAndBillingOpen(onboardingStep === "billing")

    if (onboardingStep === "meta") {
      navigate(PATH_MAP.WHATSAPP_OFFICIAL_PAGE, { replace: true })
      return
    }

    if (onboardingStep === "plan") {
      openPlan()
    } else {
      closePlan()
    }
  }, [closePlan, location.key, location.pathname, location.search, navigate, onboardingStep, openPlan])

  return (
    <>
      <Animate duration="1.5s">
        <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-1">
          <CategoryHeader
            icon={<Building2 size={18} />}
            title={t("sections.profile.title")}
            description={t("sections.profile.description")}
          />

          <TitleCardTW title={t("label_company_data")} icon={faHouse}>
            <ButtonIconTW
              size="sm"
              icon={faBoxOpen}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-900"
              onClick={() => setEnableCompany(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setEnableCompanyTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_social_media")} icon={faShareNodes}>
            <ButtonIconTW
              size="sm"
              icon={faShareNodes}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setSocialMediaOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setSocialMediaOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_whatsapp_order_support")} icon={faWhatsapp}>
            <ButtonIconTW
              size="sm"
              icon={faWhatsapp}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setWhatsappOrderSupportOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setWhatsappOrderSupportOpenTrue" />
          </TitleCardTW>

          <GetSpace top={20} />

          <CategoryHeader
            icon={<LayoutTemplate size={18} />}
            title={t("sections.public_menu.title")}
            description={t("sections.public_menu.description")}
          />

          <TitleCardTW title={t("label_public_menu")} icon={faHouse}>
            <ButtonIconTW
              size="sm"
              icon={faSquareArrowUpRight}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setPublicMenuEnable(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setPublicMenuEnableTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_public_menu_customization")} icon={faStickyNote}>
            <ButtonIconTW
              size="sm"
              icon={faStickyNote}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setPublicMenuCustomizationOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setPublicMenuCustomizationOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_public_menu_qr")} icon={faQrcode}>
            <ButtonIconTW
              size="sm"
              icon={faQrcode}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setPublicMenuQrCodeOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setPublicMenuQrCodeOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("marketing_tracking.menu_title")} icon={faBullhorn}>
            <ButtonIconTW
              size="sm"
              icon={faBullhorn}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setMarketingTrackingOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setMarketingTrackingOpenTrue" />
          </TitleCardTW>

          <GetSpace top={20} />

          <CategoryHeader
            icon={<Store size={18} />}
            title={t("sections.operations.title")}
            description={t("sections.operations.description")}
          />

          <TitleCardTW title={t("label_pickup_address")} icon={faLocationDot}>
            <ButtonIconTW
              size="sm"
              icon={faLocationDot}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setCompanyAddressEnable(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setCompanyAddressEnableTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_delivery_shipping")} icon={faTruckFast}>
            <ButtonIconTW
              size="sm"
              icon={faTruckFast}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setShippingSettingsOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setShippingSettingsOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("operational.menu_title")} icon={faGears}>
            <ButtonIconTW
              size="sm"
              icon={faGears}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setOperationalSettingsOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setOperationalSettingsOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("label_public_Time")} icon={faHouse}>
            <ButtonIconTW
              size="sm"
              icon={faClock}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setShowSchedule(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setShowScheduleTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("vacation.menu_title")} icon={faUmbrellaBeach}>
            <ButtonIconTW
              size="sm"
              icon={faUmbrellaBeach}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setVacationSettingsOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setVacationSettingsOpenTrue" />
          </TitleCardTW>

          <GetSpace top={5} />

          <TitleCardTW title={t("systemModules.menu_title")} icon={faSliders}>
            <ButtonIconTW
              size="sm"
              icon={faSliders}
              bgColor="bg-emerald-100 ring-2 ring-emerald-400"
              iconColor="text-gray-800"
              onClick={() => setCompanyModulesOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setCompanyModulesOpenTrue" />
          </TitleCardTW>

          <GetSpace top={25} />

          {userEmployee.isMaster && (
            <>
              <CategoryHeader
                icon={<Link2 size={18} />}
                title={t("sections.integrations.title")}
                description={t("sections.integrations.description")}
              />

              <GetSpace top={5} />

              <TitleCardTW title={t("label_marketplace_billing")} icon={faMoneyBillWave}>
                <ButtonIconTW
                  size="sm"
                  icon={faMoneyBillWave}
                  bgColor="bg-emerald-100 ring-2 ring-emerald-400"
                  iconColor="text-gray-800"
                  onClick={() => setPlanAndBillingOpen(true)}

                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.myCompanyPage.button.setPlanAndBillingOpenTrue" />
              </TitleCardTW>

              <GetSpace top={5} />
            </>
          )}
        </div>
      </Animate>

      {isPublicMenuQrCodeOpen && <PublicMenuQrCodePupop onClose={() => setPublicMenuQrCodeOpen(false)} />}

      {isPublicMenuCustomizationOpen && (
        <PublicMenuCustomizationPopup onClose={() => setPublicMenuCustomizationOpen(false)} />
      )}

      {isMarketingTrackingOpen && <MarketingTrackingPopup onClose={() => setMarketingTrackingOpen(false)} />}

      {isSocialMediaOpen && <SocialMediaPupop onClose={() => setSocialMediaOpen(false)} />}

      {isWhatsappOrderSupportOpen && <WhatsappOrderSupportPopup onClose={() => setWhatsappOrderSupportOpen(false)} />}

      {showSchedule && <OperatingSchedulePopup onClose={() => setShowSchedule(false)} />}

      {isCompanyEnable && <CompanyDataPupop onClonse={() => setEnableCompany(false)} />}

      {isCompanyAddressEnable && <CompanyAddressPupop onClose={() => setCompanyAddressEnable(false)} />}

      {isPublicMenuEnable && <LinkPublicMenuPupop onClonse={() => setPublicMenuEnable(false)} />}

      {isShippingSettingsOpen && <FreeShippingSettingsPupop onClose={() => setShippingSettingsOpen(false)} />}

      {isOperationalSettingsOpen && <OperationalSettingsPopup onClose={() => setOperationalSettingsOpen(false)} />}

      {isPlanAndBillingOpen && <BillingPopup onClose={() => setPlanAndBillingOpen(false)} />}

      {isVacationSettingsOpen && <VacationSettingsPopup onClose={() => setVacationSettingsOpen(false)} />}

      {isCompanyModulesOpen && (
        <CompanyModulesPopup open={isCompanyModulesOpen} onClose={() => setCompanyModulesOpen(false)} />
      )}
    </>
  )
}
